/**
 * Account Type Mapping Utilities
 *
 * Provides unified account type system across manual and Plaid accounts
 * Handles type normalization, emoji mapping, and cross-system compatibility
 */

// Unified account types across manual and Plaid
export const ACCOUNT_TYPES = {
  CHECKING: 'checking',
  SAVINGS: 'savings',
  CASH: 'cash',
  INVESTMENT: 'investment',
  RETIREMENT: 'retirement',
  CREDIT: 'credit',
};

/**
 * Map Plaid account types to unified account types
 *
 * @param {string} type - Plaid type (depository, credit, loan, investment)
 * @param {string} subtype - Plaid subtype (checking, savings, credit card, etc.)
 * @returns {string} Unified account type
 */
export function mapPlaidTypeToAccountType(type, subtype) {
  if (type === 'depository') {
    if (subtype === 'checking') return ACCOUNT_TYPES.CHECKING;
    if (subtype === 'savings') return ACCOUNT_TYPES.SAVINGS;
    return ACCOUNT_TYPES.CHECKING; // default for depository
  }
  if (type === 'credit') return ACCOUNT_TYPES.CREDIT;
  if (type === 'investment') return ACCOUNT_TYPES.INVESTMENT;
  if (type === 'loan') {
    // Could create separate loan type, but map to retirement for now
    // since retirement accounts often have loan features (401k loans)
    return ACCOUNT_TYPES.RETIREMENT;
  }

  return ACCOUNT_TYPES.CHECKING; // fallback
}

/**
 * Infer account type from manual account properties
 * Uses accountType field if available, otherwise infers from title/emoji
 *
 * @param {string} accountType - Explicit account type
 * @param {string} title - Account title/name
 * @param {string} emoji - Account emoji
 * @returns {string} Normalized account type
 */
export function normalizeAccountType(accountType, title, emoji) {
  // If explicit type is provided, use it
  if (accountType) return accountType;

  // Fallback: infer from title
  if (title) {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('check')) return ACCOUNT_TYPES.CHECKING;
    if (lowerTitle.includes('save')) return ACCOUNT_TYPES.SAVINGS;
    if (lowerTitle.includes('invest')) return ACCOUNT_TYPES.INVESTMENT;
    if (lowerTitle.includes('401k') || lowerTitle.includes('retire') || lowerTitle.includes('ira')) {
      return ACCOUNT_TYPES.RETIREMENT;
    }
    if (lowerTitle.includes('credit')) return ACCOUNT_TYPES.CREDIT;
  }

  // Fallback: infer from emoji
  if (emoji) {
    if (emoji === '💵') return ACCOUNT_TYPES.CASH;
    if (emoji === '💰') return ACCOUNT_TYPES.SAVINGS;
    if (emoji === '🏦') return ACCOUNT_TYPES.CHECKING;
    if (emoji === '📈') return ACCOUNT_TYPES.INVESTMENT;
    if (emoji === '🏖️') return ACCOUNT_TYPES.RETIREMENT;
    if (emoji === '💳') return ACCOUNT_TYPES.CREDIT;
  }

  return ACCOUNT_TYPES.CHECKING; // default fallback
}

/**
 * Get default emoji for account type
 *
 * @param {string} accountType - Unified account type
 * @returns {string} Emoji representing the account type
 */
export function getDefaultEmojiForType(accountType) {
  const typeMap = {
    checking: '🏦',
    savings: '💰',
    cash: '💵',
    investment: '📈',
    retirement: '🏖️',
    credit: '💳',
  };

  return typeMap[accountType] || '🏦';
}

/**
 * Get human-readable label for account type
 *
 * @param {string} accountType - Unified account type
 * @returns {string} Display label
 */
export function getAccountTypeLabel(accountType) {
  const labelMap = {
    checking: 'Checking',
    savings: 'Savings',
    cash: 'Cash',
    investment: 'Investment',
    retirement: 'Retirement',
    credit: 'Credit',
  };

  return labelMap[accountType] || 'Checking';
}

/**
 * Get account type sort order for grouping
 * Lower numbers appear first
 *
 * @param {string} accountType - Unified account type
 * @returns {number} Sort priority
 */
export function getAccountTypeSortOrder(accountType) {
  const orderMap = {
    checking: 1,   // Most liquid
    cash: 2,
    savings: 3,
    investment: 4,
    retirement: 5,
    credit: 6,     // Debt shown last
  };

  return orderMap[accountType] || 99;
}
